import React, {useState} from 'react'
import {Modal} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils/logUtils'
import {SHARED_FOLDER_ICON} from './constants'
import {shareErrorMessage} from './errors'
import {accessLabel, formatShareDate} from './format'
import {useAnswerShareInvitation, usePendingShareInvitations} from './managementHooks'
import {OwnedBy} from './OwnedBy'
import type {PendingShareInvitation} from './types'
import './sharing.scss'

/** How many invitations are waiting for the signed-in user; 0 when sharing is off. */
export function usePendingShareInvitationCount(): number {
  const pending = usePendingShareInvitations()
  return pending.data?.invitations.length ?? 0
}

/**
 * The count of pending invitations, for the Shared root's entry point (P1-F5). Renders nothing
 * when there are none, or when sharing is off.
 */
export const PendingShareInvitesBadge: React.FC<{className?: string}> = ({className}) => {
  const count = usePendingShareInvitationCount()
  if (count === 0) return null
  return (
    <span
      className={`badge rounded-pill bg-primary ob-sharing-badge ${className ?? ''}`.trim()}
      data-testid='sharing-pending-badge'
    >
      {count}
      <span className='visually-hidden'>
        {count === 1 ? ' pending invitation' : ' pending invitations'}
      </span>
    </span>
  )
}

/** Accept or decline each of my pending invitations. */
export const PendingShareInvitationsList: React.FC = () => {
  const pending = usePendingShareInvitations()
  const answer = useAnswerShareInvitation()
  const [busy, setBusy] = useState<string | null>(null)
  const invitations = pending.data?.invitations ?? []

  const respond = (invite: PendingShareInvitation, choice: 'accept' | 'decline') => {
    setBusy(invite.inviteId)
    answer.mutate(
      {shareId: invite.shareId, inviteId: invite.inviteId, answer: choice},
      {
        onSuccess: () =>
          toast.success(
            choice === 'accept'
              ? `“${invite.shareName}” is now in your Shared folders`
              : `Declined the invitation to “${invite.shareName}”`,
            toastDefaultConfig
          ),
        onError: (err) => {
          toast.error(
            shareErrorMessage(err, {
              overrides: {already_member: "You're already a member of this folder."},
            }),
            toastDefaultConfig
          )
          getLogger().warn('Answering a share invitation failed', err)
        },
        onSettled: () => setBusy(null),
      }
    )
  }

  if (pending.isLoading) {
    return <div className='text-muted small'>Loading invitations…</div>
  }
  if (pending.isError) {
    return (
      <div className='alert alert-warning small mb-0' role='alert'>
        {shareErrorMessage(pending.error)}
      </div>
    )
  }
  if (invitations.length === 0) {
    return <p className='text-muted mb-0'>You have no pending invitations.</p>
  }
  return (
    <ul className='list-unstyled mb-0 ob-sharing-list' aria-label='Pending invitations'>
      {invitations.map((invite) => (
        <li
          key={invite.inviteId}
          className='ob-sharing-list__row'
          data-testid='sharing-pending-row'
        >
          <div className='ob-sharing-list__main'>
            <div className='fw-semibold text-truncate'>
              <i className={`${SHARED_FOLDER_ICON} me-2 text-primary`} aria-hidden='true' />
              {invite.shareName}
            </div>
            <div className='small'>
              <OwnedBy ownerName={invite.ownerName} access={invite.access} />
            </div>
            <div className='text-muted small'>
              {accessLabel(invite.access)} · Expires {formatShareDate(invite.expiresAt)}
            </div>
          </div>
          <div className='ob-sharing-list__actions'>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={() => respond(invite, 'decline')}
              disabled={busy === invite.inviteId}
              aria-label={`Decline the invitation to ${invite.shareName}`}
            >
              Decline
            </button>
            <button
              type='button'
              className='btn btn-sm btn-primary'
              onClick={() => respond(invite, 'accept')}
              disabled={busy === invite.inviteId}
              aria-label={`Accept the invitation to ${invite.shareName}`}
            >
              Accept
            </button>
          </div>
        </li>
      ))}
    </ul>
  )
}

export const PendingShareInvitationsDialog: React.FC<{onHide: () => void}> = ({onHide}) => (
  <Modal show onHide={onHide} centered scrollable aria-labelledby='share-invitations-title'>
    <Modal.Header closeButton>
      <Modal.Title id='share-invitations-title'>
        <i className={`${SHARED_FOLDER_ICON} me-2`} aria-hidden='true' />
        Shared folder invitations
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <PendingShareInvitationsList />
    </Modal.Body>
    <Modal.Footer>
      <button type='button' className='btn btn-light' onClick={onHide}>
        Close
      </button>
    </Modal.Footer>
  </Modal>
)

/**
 * A Files-panel header button that appears only while invitations are pending: the badge plus the
 * list. Stand-in for the Shared root's entry point until that root exists (P1-F3); move the badge
 * there and drop this when it does.
 */
export const PendingShareInvitationsButton: React.FC = () => {
  const count = usePendingShareInvitationCount()
  const [open, setOpen] = useState(false)
  if (count === 0 && !open) return null
  const label = `${count} shared folder ${count === 1 ? 'invitation' : 'invitations'}`
  return (
    <>
      <button
        type='button'
        className='ob-files-panel__icon-btn position-relative'
        onClick={() => setOpen(true)}
        title={label}
        aria-label={label}
        data-testid='sharing-pending-button'
      >
        <i className={SHARED_FOLDER_ICON} aria-hidden='true' />
        <PendingShareInvitesBadge className='ob-sharing-badge--corner' />
      </button>
      {open && <PendingShareInvitationsDialog onHide={() => setOpen(false)} />}
    </>
  )
}
